/**
 * @jsxRuntime classic
 * @jsx jsx
 */
/* eslint-disable @atlaskit/ui-styling-standard/no-imported-style-values -- Shared styles from legacy AdminConsentModal for consistency. See go/DSP-18766 */

import { jsx } from '@compiled/react';
import { useState } from 'react';

import ModalDialog from '@atlaskit/modal-dialog/modal-dialog';
import ModalTransition from '@atlaskit/modal-dialog/modal-transition';
import AKModalFooter from '@atlaskit/modal-dialog/modal-footer';
import ModalBody from '@atlaskit/modal-dialog/modal-body';
import ModalTitle from '@atlaskit/modal-dialog/modal-title';
import ModalHeader from '@atlaskit/modal-dialog/modal-header';
import Button from '@atlaskit/button/default/button';
import { Box, Text, Stack, Inline, Flex } from '@atlaskit/primitives/compiled';
import Heading from '@atlaskit/heading/heading';
import Link from '@atlaskit/link/link';
import type {
	EgressSetPayload,
	RemoteSetPayload,
} from '../../../custom-ui/bridge/methods/permissions/types';
import { styles, messages } from '../../../custom-ui/bridge/methods/permissions/AdminConsentModal';
import { useIntl } from 'react-intl';

// ─── Types ────────────────────────────────────────────────────────────────────

export type ManifestRemoteMap = Record<
	string,
	| {
			description: string;
			name: string;
			supportedPatterns: string[];
	  }
	| undefined
>;

export type DomainInfoEgress = {
	payload: EgressSetPayload;
	type: 'EGRESS';
};

export type DomainInfoRemote = {
	manifestRemoteMap: ManifestRemoteMap;
	payload: RemoteSetPayload;
	type: 'REMOTE';
};

export type AdminConsentModalProps = {
	appName: string;
	domainInfo: DomainInfoEgress | DomainInfoRemote;
	onCancel: () => void;
	onConfirm: () => Promise<void>;
	privacyPolicyLink?: string | null;
	termsOfUseLink?: string | null;
};

// ─── Component ────────────────────────────────────────────────────────────────

const getHumanReadableName = (operation: string): string => {
	switch (operation) {
		case 'FETCH_BACKEND_SIDE':
			return 'Fetch backend side';
		case 'FETCH_CLIENT_SIDE':
			return 'Fetch client side';
		case 'FONTS':
			return 'Fonts';
		case 'FRAMES':
			return 'Frames';
		case 'IMAGES':
			return 'Images';
		case 'MEDIA':
			return 'Media';
		case 'NAVIGATION':
			return 'Navigation';
		case 'SCRIPTS':
			return 'Scripts';
		case 'STYLES':
			return 'Styles';
		default:
			return 'Egress';
	}
};

/**
 * Admin consent modal for the v2 stateless bridge pattern.
 *
 * Unlike the legacy `AdminConsentModal` in `custom-ui/bridge/methods/permissions/`,
 * this component manages its own open/closed state so that `<ModalTransition>` can
 * play the exit animation before the callbacks fire. The overlay controller does not
 * need to call `.hide()` — once closed the component simply renders an empty
 * `<ModalTransition>` which has no visible effect.
 */
export const AdminConsentModal = ({
	appName,
	domainInfo,
	onCancel,
	onConfirm,
	termsOfUseLink,
	privacyPolicyLink,
}: AdminConsentModalProps): JSX.Element => {
	const [isOpen, setIsOpen] = useState(true);
	const intl = useIntl();

	const handleCancel = () => {
		setIsOpen(false);
		onCancel();
	};

	const handleConfirm = async () => {
		try {
			await onConfirm();
			setIsOpen(false);
		} catch {
			// API failed — keep modal open so the user can retry or decline
		}
	};

	const renderDomains = () => {
		if (domainInfo.type === 'REMOTE') {
			return (
				<Flex direction="column" gap="space.150">
					{domainInfo.payload.remotes.map((r) => {
						const manifestRemoteInfo = domainInfo.manifestRemoteMap[r.key];
						return (
							<Stack key={r.key} space="space.150">
								<Heading size="small">
									{manifestRemoteInfo ? manifestRemoteInfo.description : 'Remote'}
								</Heading>
								<Inline xcss={styles.rowContainer} spread="space-between" grow="fill">
									<Text>
										{r.configured.endpoint === null
											? 'This remote is being reset to the default value'
											: r.configured.endpoint}
									</Text>
									<Text>{manifestRemoteInfo ? manifestRemoteInfo.name : null}</Text>
								</Inline>
							</Stack>
						);
					})}
				</Flex>
			);
		}

		return (
			<Flex direction="column" gap="space.150">
				{domainInfo.payload.egresses.map((e) => (
					<Stack key={e.key} space="space.150">
						<Heading size="xsmall">{e.description}</Heading>
						{e.configured.map((c) =>
							c.type.map((t) => (
								<Inline key={t} xcss={styles.rowContainer} spread="space-between" grow="fill">
									<Text>{c.domain}</Text>
									<Text>{getHumanReadableName(t)}</Text>
								</Inline>
							)),
						)}
					</Stack>
				))}
			</Flex>
		);
	};

	const Notice = () => {
		if (termsOfUseLink && privacyPolicyLink) {
			return (
				<Text>
					{intl.formatMessage(messages.noticeForMoreDetails, { appName })}{' '}
					<Link href={termsOfUseLink}>{intl.formatMessage(messages.termsOfUseLink)}</Link>{' '}
					{intl.formatMessage(messages.noticeAnd)}{' '}
					<Link href={privacyPolicyLink}>{intl.formatMessage(messages.privacyPolicyLink)}</Link>
				</Text>
			);
		}
		if (termsOfUseLink) {
			return (
				<Text>
					{intl.formatMessage(messages.noticeForMoreDetails, { appName })}{' '}
					<Link href={termsOfUseLink}>{intl.formatMessage(messages.termsOfUseLink)}</Link>
				</Text>
			);
		}
		if (privacyPolicyLink) {
			return (
				<Text>
					{intl.formatMessage(messages.noticeForMoreDetails, { appName })}{' '}
					<Link href={privacyPolicyLink}>{intl.formatMessage(messages.privacyPolicyLink)}</Link>
				</Text>
			);
		}
		return null;
	};

	const ModalFooter = () => (
		<Box xcss={styles.modalFooterContainer}>
			<AKModalFooter>
				<Inline space="space.075">
					<Button onClick={handleCancel}>{intl.formatMessage(messages.declineButtonLabel)}</Button>
					<Button onClick={handleConfirm} appearance="primary">
						{intl.formatMessage(messages.approveButtonLabel)}
					</Button>
				</Inline>
			</AKModalFooter>
		</Box>
	);

	return (
		<ModalTransition>
			{isOpen && (
				<ModalDialog onClose={handleCancel} width="large">
					<ModalHeader>
						<Box xcss={styles.titleContainer}>
							<ModalTitle>{intl.formatMessage(messages.modalTransitionTitle)}</ModalTitle>
						</Box>
					</ModalHeader>
					<ModalBody>
						<Stack space="space.300">
							<Text>{intl.formatMessage(messages.modalTransitionText, { appName })}</Text>
							<Box>
								{renderDomains()}
								<Box xcss={styles.disclaimerContainer}>
									<Notice />
								</Box>
							</Box>
						</Stack>
					</ModalBody>
					<ModalFooter />
				</ModalDialog>
			)}
		</ModalTransition>
	);
};
